import { Box, Typography, Grid, Breadcrumbs, Button } from "@mui/material";
import React, { useMemo } from "react";
import { observer } from "mobx-react";
import { PropertySearchListModel } from "app/modules/main/api/main-clients.api";
import GoogleMaps from "./google-maps";
import PropertyPopup from "./property-popup";
import { ResultItemCard } from "./result-item";
import { useInstance } from "react-ioc";
import { SearchStore } from "./SearchStore";
import { MainStore } from "app/modules/main/stores/MainStore";

interface MapViewProps {
    items: PropertySearchListModel[];
}

export default observer(({ items }: MapViewProps) => {
    const searchStore = useInstance(SearchStore);
    const mainStore = useInstance(MainStore);
    const validItems = useMemo(() =>
        items.filter(item => item.latitude && item.longitude),
        [items]
    );

    let state = searchStore.searchParams.state?.length > 0 ? mainStore.clientData.states.find(x => x.code == searchStore.searchParams.state) : null;

    let bread = <Breadcrumbs separator="›">
        {searchStore.searchParams.state?.length > 0 ? <Button variant={"text"} sx={{ minWidth: 0, padding: 0, fontSize: "12px" }} onClick={() => searchStore.clickOnCountry()}>
            USA
        </Button> : <Typography color="text.primary" sx={{ fontSize: "12px" }}>USA</Typography>}

        {searchStore.searchParams.city?.length > 0 ?
            <Button variant={"text"} sx={{ minWidth: 0, padding: 0, fontSize: "12px" }} onClick={() => searchStore.clickOnState()}>
                {state?.name}
            </Button> : state?.name?.length > 0 && <Typography color="text.primary" sx={{ fontSize: "12px" }}>{state?.name}</Typography>}

        {searchStore.searchParams.city?.length > 0 && <Typography color="text.primary" sx={{ fontSize: "12px" }}>{searchStore.searchParams.city}</Typography>}
    </Breadcrumbs>;

    return (
        <Grid container>
            {/* Mobile Layout */}
            <Grid item xs={12} sx={{ display: { xs: "block", md: "none" }, p: 1 }}>
                <Grid container>
                    <Grid item xs={12} sx={{ pb: 2 }}>{bread}</Grid>
                    <Grid item xs={12}><Typography variant={"h6"} align={"center"}>{searchStore.getTitle() + " Vacation Rentals"}</Typography></Grid>
                    <Grid item xs={12} textAlign={"center"}>
                        {items.length} rentals
                        {validItems.length !== items.length && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                ({validItems.length} with location data)
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                {/* Google Maps component */}
                <Box sx={{ mb: 2 }}>
                    <GoogleMaps
                        items={validItems}
                        height="400px"
                    />
                </Box>

                {/* Property cards */}
                <Grid container spacing={1}>
                    {items.map((item, index) => {
                        // Find the index of this item in the validItems array for map numbering
                        const mapIndex = validItems.findIndex(validItem => validItem.id === item.id);
                        const displayNumber = mapIndex >= 0 ? mapIndex + 1 : null;

                        return (
                            <Grid item xs={12} sm={6} key={item.id} sx={{ p: 1 }}>
                                <Box sx={{ position: "relative" }}>
                                    <ResultItemCard item={item} />
                                    {/* Property number badge - only show if item has coordinates */}
                                    {displayNumber && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                zIndex: 1,
                                                backgroundColor: "#1976d2",
                                                color: "white",
                                                borderRadius: "50%",
                                                width: 24,
                                                height: 24,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                                            }}
                                        >
                                            {displayNumber}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Grid>

            {/* Desktop Layout */}
            <Grid item xs={12} sx={{ display: { xs: "none", md: "block" }, pl: 2, pr: 2 }}>
                <Grid container>
                    <Grid item xs={8}><Typography variant={"h6"}>{searchStore.getTitle() + " Vacation Rentals"}</Typography></Grid>
                    <Grid item xs={4} textAlign={"right"} sx={{ pr: 2 }}>
                        {items.length} rentals
                        {validItems.length !== items.length && (
                            <Typography variant="body2" color="text.secondary">
                                ({validItems.length} with location data)
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>{bread}</Grid>
                </Grid>

                {/* Google Maps component */}
                <Box sx={{ mb: 2 }}>
                    <GoogleMaps
                        items={validItems}
                        height="500px"
                    />
                </Box>

                {/* Property cards */}
                <Grid container spacing={1}>
                    {items.map((item, index) => {
                        // Find the index of this item in the validItems array for map numbering
                        const mapIndex = validItems.findIndex(validItem => validItem.id === item.id);
                        const displayNumber = mapIndex >= 0 ? mapIndex + 1 : null;

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.id} sx={{ p: 1 }}>
                                <Box sx={{ position: "relative" }}>
                                    <ResultItemCard item={item} />
                                    {/* Property number badge - only show if item has coordinates */}
                                    {displayNumber && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                zIndex: 1,
                                                backgroundColor: "#1976d2",
                                                color: "white",
                                                borderRadius: "50%",
                                                width: 24,
                                                height: 24,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                                            }}
                                        >
                                            {displayNumber}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Grid>

            {/* Property Popup */}
            <PropertyPopup
                open={searchStore.isPopupOpen}
                onClose={searchStore.closePropertyPopup}
                property={searchStore.popupProperty}
            />
        </Grid>
    );
});
